import './PathMap.css'

import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { useState, useEffect } from 'react'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { useTranslations } from '@/hooks/useTranslations'
import { PathPoint } from '@/components/Map/PathMap'
import { defaultMapCenter } from '@/env/config'
import { LatLngExpression } from 'leaflet'
import { MapContainer, Marker, Polygon, Polyline, TileLayer } from 'react-leaflet'
import L from 'leaflet'
import { HistoryUser, MAP_COLORS, UserDeviceHistories, UserDeviceHistoriesData, UserDeviceHistoriesMachine } from '@/pages/communal/statistics/requests/histories'

function findLastPoint(data: UserDeviceHistories | undefined): PathPoint | undefined {
  if (data === undefined) {
    return
  }
  let point: PathPoint | undefined
  const orderedKeys = Object.keys(data).sort();
  for (let i = 0; i < orderedKeys.length; i++) {
    const row = data[orderedKeys[i]]
    if (!row.path || row.path.length === 0) {
      continue
    }
    const currentPoint = row.path[row.path.length - 1]
    if (!point) {
      point = currentPoint
      continue
    }
    if (new Date(point.time) < new Date(currentPoint.time)) {
      point = currentPoint
    }
  }
  return point
}

export const CommunalMachinePathMap = ({
  services,
  session,
  historiesData,
  selectedUser,
  setSelectedUser
}: {
  services: Services,
  session: CurrentSession,
  historiesData: UserDeviceHistoriesData
  selectedUser: HistoryUser | undefined
  setSelectedUser: Dispatch<SetStateAction<HistoryUser | undefined>>
}) => {
  const translates = useTranslations(services, session)
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  const lastPoint = useMemo(() => {
    return findLastPoint(historiesData?.histories)
  }, [historiesData]);

  if (!historiesData || translates === undefined) {
    return <div className='spinner'></div>
  }
  
  const center: LatLngExpression = (lastPoint && {
    lat: lastPoint.lat,
    lng: lastPoint.lng
  }) || myposition || defaultMapCenter

  return <div className='machine-pathmap-wrapper'>
    <MapContainer attributionControl={false} center={center} zoom={13} style={{ height: '100%', minHeight: '250px', width: '100%' }}
      key={historiesData.hash} >
      <TileLayer
        attribution='evoltag.pl'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {selectedUser ? Object.entries(historiesData.histories).filter(([, row]) => row.userId === selectedUser.id).map((([, row]) => {
        return row.dividedPaths?.map((path) => (
          <Polyline
            positions={path?.map((prow) => ([prow.lat, prow.lng])) || []}
            pathOptions={{ color: historiesData.users.find(u => u.id === row.userId)?.color || MAP_COLORS[0], weight: 4 }}
          />
        ))
      })) : Object.entries(historiesData.histories).map(([key, row]) => row.dividedPaths?.map((path) => (
        <Polyline
          positions={path?.map((prow) => ([prow.lat, prow.lng])) || []}
          pathOptions={{ color: historiesData.users.find(u => u.id === row.userId)?.color || MAP_COLORS[0], weight: 4 }}
        />
      )))}

      {myposition ? <Marker
        key='my-location'
        position={myposition}
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px" />`,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        })}></Marker> : null}
    </MapContainer>

    <ul className='users-nav'>
      {Object.entries(historiesData.users).map(([key, user]) => (<li
        className={`row ${user.id === selectedUser?.id ? 'selected' : ''}`}
        onClick={() => user.id === selectedUser?.id ? setSelectedUser(undefined) : setSelectedUser(user)}>
        {user.username || user.email || user.id}
      </li>))}
    </ul>
  </div>
}
